import { Alert, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import type { MouseEvent } from 'react';
import useLocalization from '../../../../hooks/useLocalization';
import useKycAttempts from '../../../../hooks/useKycAttempts';
import BalanceContact from '../../../atoms/BalanceContact';
import { FormattedBalance } from '../../../atoms/FormattedBalance';
import KycVerificationPanel from '../../../molecules/KycVerificationPanel';
import PaymentFormRenderer from '../../../organisms/PaymentFormRenderer';
import { PAYMENT_TRANSACTION_TYPE } from '../../../../types/enums';
import { usePaymentFlowState } from '../../../../hooks/payment/usePaymentFlowState';
import { useWithdrawalBonusGuard } from '../../../../hooks/payment/useWithdrawalBonusGuard';
import { SkinnedPaymentProviders } from '../../../../config/skinRegistry/components';
import { isActiveWelcomeBonus } from '../../../../queries/bonus';
import { useStartKycVerification, useUserData } from '../../../../queries';
import { useMarketplaceConfig } from '../../../../queries/marketplace';
import ForfeitBonusDialog from '../../../molecules/ForfeitBonusDialog';
import { shouldShowTransactionKyc } from '../../../../utils/kyc';
import WelcomeBonusSelectionGate from '../../../molecules/WelcomeBonusSelectionGate';
import SwipeableModalShell from '../../../atoms/SwipeableModalShell';
import NeobetWalletModalHeader from '../molecules/NeobetWalletModalHeader';
import NeobetRaisedActionButtonLabel from '../molecules/NeobetRaisedActionButtonLabel';
import BonusMinimumDepositNotice from '../../../molecules/BonusMinimumDepositNotice';

const styles = {
  root: {
    p: { xs: 2, md: 3 },
  },
  content: {
    mt: 2,
    gap: 2,
    flex: 1,
    minHeight: { xs: 400, md: 420 },
    overflow: 'hidden',
  },
  scrollArea: {
    gap: 2,
    pr: { xs: 0.5, md: 0 },
  },
  toggleGroup: {
    display: 'flex',
    backgroundColor: 'neutral.600',
    borderRadius: 2.5,
    p: 0,
    border: '1px solid',
    borderColor: 'neutral.500',
  },
  toggleButton: {
    flex: 1,
    height: 40,
    backgroundColor: 'transparent',
    color: 'neutral.300',
    borderRadius: 2,
    border: 'none',
    typography: 'body3',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'neutral.500',
    },
    '&.Mui-selected': {
      color: 'neutral.25',
      backgroundColor: 'primary.main',
      fontWeight: 900,
      '&:hover': {
        backgroundColor: 'primary.main',
      },
    },
  },
} as const;

const NeobetBalanceModal = ({
  handleClose,
  showBalanceContact,
}: {
  handleClose: () => void;
  showBalanceContact: boolean;
}) => {
  const { t } = useLocalization();
  const { data: userData } = useUserData();
  const { data: marketplaceConfig } = useMarketplaceConfig();
  const {
    transactionType,
    resolvedPaymentProvider,
    paymentGateway,
    paymentProviders,
    isDeposit,
    isWithdrawal,
    cryptoCashier,
    showPaymentProviderPicker,
    canShowPaymentForm,
    withdrawalDisabled,
    withdrawalDisabledReason,
    handleTransactionTypeChange,
    handleProviderChange,
    resetToDeposit,
  } = usePaymentFlowState();

  const {
    firstDepositBonus,
    isForfeitDialogOpen,
    shouldBlockWithdrawal,
    setWithdrawalBonusCleared,
    closeForfeitDialog,
    handleForfeitCancel,
  } = useWithdrawalBonusGuard({ isWithdrawal, resetToDeposit });
  const { mutate: startVerification, isPending: isVerificationPending } = useStartKycVerification();
  const { hasReachedLimit } = useKycAttempts(userData?.kycAttempts);
  const marketplaceKycConfig = marketplaceConfig?.marketplaceConfig.kyc;
  const showForm = canShowPaymentForm && !shouldBlockWithdrawal;
  const shouldShowKycGate = shouldShowTransactionKyc(userData, marketplaceKycConfig, transactionType);

  const handleTransactionTypeChangeWrapper = (
    _event: MouseEvent<HTMLElement>,
    newType: typeof transactionType | null
  ) => {
    handleTransactionTypeChange(newType);
  };

  return (
    <SwipeableModalShell testId='neobet-balance-modal-shell' sx={styles.root}>
      <NeobetWalletModalHeader onClose={handleClose} balanceContent={<FormattedBalance balanceType='main' />} />

      <Stack sx={styles.content} data-testid='neobet-balance-modal-content'>
        <ToggleButtonGroup
          value={transactionType}
          exclusive
          onChange={handleTransactionTypeChangeWrapper}
          aria-label={t('transactionType')}
          sx={styles.toggleGroup}
        >
          <ToggleButton sx={styles.toggleButton} value={PAYMENT_TRANSACTION_TYPE.DEPOSIT}>
            <NeobetRaisedActionButtonLabel inactive={!isDeposit}>{t('deposit')}</NeobetRaisedActionButtonLabel>
          </ToggleButton>
          <ToggleButton
            sx={styles.toggleButton}
            value={PAYMENT_TRANSACTION_TYPE.WITHDRAW}
            disabled={withdrawalDisabled}
          >
            <NeobetRaisedActionButtonLabel inactive={!isWithdrawal}>{t('withdraw')}</NeobetRaisedActionButtonLabel>
          </ToggleButton>
        </ToggleButtonGroup>
        {withdrawalDisabledReason === 'provider_unavailable' && (
          <Alert severity='info'>{t('zeroXWithdrawalUnavailable')}</Alert>
        )}

        <SwipeableModalShell.ScrollArea sx={styles.scrollArea} testId='neobet-balance-modal-scroll-area'>
          <WelcomeBonusSelectionGate enabled={isDeposit}>
            {shouldShowKycGate ? (
              <KycVerificationPanel
                attemptsExceeded={hasReachedLimit}
                isPending={isVerificationPending}
                onStartVerification={() => startVerification()}
                status={userData?.kycStatus}
                trigger={marketplaceKycConfig?.trigger}
              />
            ) : (
              <>
                {isDeposit && <BonusMinimumDepositNotice bonus={firstDepositBonus} />}
                {!shouldBlockWithdrawal && showPaymentProviderPicker && (
                  <SkinnedPaymentProviders
                    transactionType={transactionType}
                    selectedProvider={resolvedPaymentProvider}
                    onProviderChange={handleProviderChange}
                    paymentProviders={paymentProviders}
                    paymentGateway={paymentGateway}
                  />
                )}

                {!shouldBlockWithdrawal && showBalanceContact && <BalanceContact />}

                {showForm && (
                  <PaymentFormRenderer
                    cryptoCashier={cryptoCashier}
                    paymentGateway={paymentGateway}
                    transactionType={transactionType}
                    paymentProvider={resolvedPaymentProvider}
                  />
                )}
              </>
            )}
          </WelcomeBonusSelectionGate>
        </SwipeableModalShell.ScrollArea>
      </Stack>
      {firstDepositBonus && isActiveWelcomeBonus(firstDepositBonus) && (
        <ForfeitBonusDialog
          bonus={firstDepositBonus}
          onCancel={handleForfeitCancel}
          onClose={closeForfeitDialog}
          onSuccess={() => {
            setWithdrawalBonusCleared(true);
            closeForfeitDialog();
          }}
          open={isForfeitDialogOpen}
          productType={firstDepositBonus.campaignProduct}
        />
      )}
    </SwipeableModalShell>
  );
};

export default NeobetBalanceModal;
